import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";
import { Ship, Train, Anchor, Package, Loader2, Filter, CheckCircle2, ExternalLink, RefreshCw, X, Mail, Phone, Building2, ArrowRight, UserPlus, Calendar, MapPin } from "lucide-react";

const STATUSES = [
  { key: "", label: "All" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "quoted", label: "Quoted" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const STATUS_STYLES = {
  new: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  in_progress: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  quoted: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  won: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  lost: "bg-red-500/20 text-red-300 border-red-500/30",
};

const MODE_META = {
  ocean_fcl: { label: "Ocean · FCL", icon: Ship },
  ocean_lcl: { label: "Ocean · LCL", icon: Anchor },
  rail: { label: "Rail", icon: Train },
};

const StatusPill = ({ status }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.new}`}>
    {status?.replace("_", " ") || "new"}
  </span>
);

const StatTile = ({ label, value, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`text-left rounded-2xl border p-4 transition ${
    active ? "bg-[var(--tt-green)]/10 border-[var(--tt-green)]" : "bg-white/5 border-white/10 hover:border-white/30"
  }`}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-white/50">{label}</div>
    <div className="font-display font-black text-3xl text-white mt-1">{value}</div>
  </button>
);

const Detail = ({ label, value, testid }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{label}</div>
    <div className="text-sm text-white mt-0.5 break-words" data-testid={testid}>{value || <span className="text-white/40">—</span>}</div>
  </div>
);

const QuoteDrawer = ({ id, onClose, onChanged }) => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [convertMsg, setConvertMsg] = useState("");

  useEffect(() => {
    if (!id) return;
    api.getFreightQuote(id).then((r) => setQ(r.data.quote)).catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [id]);

  const changeStatus = async (status) => {
    setBusy(true); setErr("");
    try {
      const r = await api.updateFreightQuote(id, { status });
      setQ(r.data.quote);
      onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const convert = async () => {
    setBusy(true); setErr(""); setConvertMsg("");
    try {
      const r = await api.convertFreightQuoteToLead(id);
      setConvertMsg(r.data.already_linked ? `Already linked to lead ${r.data.lead.id.slice(0, 8)}.` : `Lead created: ${r.data.lead.company}`);
      const refreshed = await api.getFreightQuote(id);
      setQ(refreshed.data.quote);
      onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Convert failed"); }
    finally { setBusy(false); }
  };

  if (!id) return null;

  const mode = MODE_META[q?.mode] || { label: q?.mode || "?", icon: Package };
  const Icon = mode.icon;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="freight-quote-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#0f1512] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0f1512] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-xl bg-[var(--tt-green)]/20 text-[var(--tt-green)]">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-white">{q?.origin || "…"} → {q?.destination || "…"}</div>
              <div className="text-xs text-white/50">{mode.label} · {q?.created_at?.slice(0, 10)}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="freight-drawer-close" className="text-white/60 hover:text-white p-1"><X className="h-5 w-5" /></button>
        </div>

        {!q && !err && <div className="p-6 text-sm text-white/60 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {err && <div className="p-6 text-sm text-red-400">{err}</div>}
        {q && (
          <div className="p-6 space-y-6">
            {/* Status controls */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Status</div>
              <div className="flex flex-wrap gap-2" data-testid="freight-status-picker">
                {STATUSES.filter((s) => s.key).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => changeStatus(s.key)}
                    disabled={busy || q.status === s.key}
                    data-testid={`freight-status-btn-${s.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                      q.status === s.key
                        ? `${STATUS_STYLES[s.key]} border-current`
                        : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Contact</div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-white font-semibold">
                  <UserPlus className="h-4 w-4 text-[var(--tt-green)]" />
                  {q.contact_name}
                  {q.company && <span className="text-white/50 font-normal">· {q.company}</span>}
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Mail className="h-3.5 w-3.5 text-white/50" />
                  <a href={`mailto:${q.email}`} className="hover:text-[var(--tt-green)]">{q.email}</a>
                </div>
                {q.phone && (
                  <div className="flex items-center gap-2 text-white/80">
                    <Phone className="h-3.5 w-3.5 text-white/50" />
                    <a href={`tel:${q.phone}`} className="hover:text-[var(--tt-green)]">{q.phone}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Route + Cargo */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Shipment</div>
              <div className="grid sm:grid-cols-2 gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
                <Detail label="Mode" value={mode.label} testid="freight-detail-mode" />
                <Detail label="Route" value={`${q.origin} → ${q.destination}`} />
                <Detail label="Origin port" value={q.origin_port} />
                <Detail label="Destination port" value={q.destination_port} />
                {q.mode === "ocean_fcl" && (
                  <Detail label="Container" value={`${q.container_count || 1} × ${q.container_size || "—"}`} />
                )}
                {(q.mode === "ocean_lcl" || q.mode === "rail") && (
                  <Detail label="Volume" value={q.cbm ? `${q.cbm} CBM` : "—"} />
                )}
                {q.weight_kg && <Detail label="Weight" value={`${q.weight_kg} kg`} />}
                <Detail label="Commodity" value={q.commodity} />
                <Detail label="Incoterms" value={q.incoterms} />
                <Detail label="Cargo ready" value={q.cargo_ready_date} />
                {q.hazardous && <Detail label="Hazardous" value="Yes · needs DG docs" />}
              </div>
              {q.notes && (
                <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-1">Customer notes</div>
                  <div className="text-sm text-white/90 whitespace-pre-wrap">{q.notes}</div>
                </div>
              )}
            </div>

            {/* Convert / linked lead */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Sales</div>
              {q.lead_id ? (
                <Link to={`/admin/leads/${q.lead_id}`} data-testid="freight-linked-lead" className="flex items-center justify-between rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 hover:bg-emerald-500/20 transition">
                  <div>
                    <div className="text-emerald-300 text-xs font-semibold uppercase tracking-widest">Linked to lead</div>
                    <div className="text-white font-mono text-xs mt-0.5">{q.lead_id.slice(0, 8)}…</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-emerald-300" />
                </Link>
              ) : (
                <button onClick={convert} disabled={busy} data-testid="freight-convert-btn" className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--tt-green)] hover:bg-emerald-400 text-slate-950 font-bold text-sm py-3 transition disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  Convert to lead
                </button>
              )}
              {convertMsg && <div className="text-xs text-emerald-300 mt-2">{convertMsg}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminFreightQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    api.listFreightQuotes(status || null)
      .then((r) => { setQuotes(r.data.quotes || []); setCounts(r.data.counts || {}); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const filtered = useMemo(() => quotes, [quotes]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-white text-3xl tracking-tight">Freight quotes</h1>
          <p className="text-white/60 text-sm mt-1">Incoming ocean &amp; rail quote requests. Convert into leads to work them.</p>
        </div>
        <button onClick={load} data-testid="freight-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/5 border border-white/10 rounded-full px-3 py-2 transition">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatTile label="Total" value={total} active={status === ""} onClick={() => setStatus("")} testid="freight-stat-total" />
        {STATUSES.filter((s) => s.key).map((s) => (
          <StatTile
            key={s.key}
            label={s.label}
            value={counts[s.key] || 0}
            active={status === s.key}
            onClick={() => setStatus(s.key)}
            testid={`freight-stat-${s.key}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-white/60 text-sm">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-10 text-center" data-testid="freight-quotes-empty">
          <Package className="h-10 w-10 text-white/30 mx-auto mb-3" />
          <div className="text-white font-semibold">No freight quotes {status ? `with status "${status}"` : "yet"}</div>
          <div className="text-sm text-white/50 mt-1">Submissions from <span className="font-mono">/freight/quote</span> and route pages land here.</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="freight-quotes-list">
          <div className="hidden md:grid grid-cols-[110px_1.4fr_1fr_1fr_120px_120px] gap-3 px-4 py-3 text-[10px] uppercase tracking-widest text-white/50 font-semibold border-b border-white/10 bg-white/5">
            <div>Mode</div>
            <div>Route</div>
            <div>Contact</div>
            <div>Cargo</div>
            <div>Status</div>
            <div className="text-right">Received</div>
          </div>
          {filtered.map((q) => {
            const m = MODE_META[q.mode] || { label: q.mode, icon: Package };
            const Icon = m.icon;
            const cargo = q.mode === "ocean_fcl"
              ? `${q.container_count || 1}× ${q.container_size || "container"}`
              : q.cbm ? `${q.cbm} CBM` : "—";
            return (
              <button
                key={q.id}
                onClick={() => setOpenId(q.id)}
                data-testid={`freight-quote-row-${q.id}`}
                className="w-full text-left border-b border-white/10 last:border-0 hover:bg-white/5 transition"
              >
                <div className="md:grid md:grid-cols-[110px_1.4fr_1fr_1fr_120px_120px] md:gap-3 px-4 py-3 items-center flex flex-col gap-2 md:flex-row">
                  <div className="flex items-center gap-2 text-white text-xs font-semibold">
                    <Icon className="h-3.5 w-3.5 text-[var(--tt-green)]" /> {m.label}
                  </div>
                  <div className="text-white text-sm">
                    <div className="font-semibold">{q.origin} → {q.destination}</div>
                    {(q.origin_port || q.destination_port) && (
                      <div className="text-[11px] text-white/50 flex items-center gap-1"><MapPin className="h-3 w-3" /> {q.origin_port || "?"} → {q.destination_port || "?"}</div>
                    )}
                  </div>
                  <div className="text-white/90 text-sm">
                    <div>{q.contact_name}</div>
                    <div className="text-[11px] text-white/50">{q.email}</div>
                  </div>
                  <div className="text-white/70 text-sm">
                    <div>{cargo}</div>
                    <div className="text-[11px] text-white/50">{q.commodity || "—"}</div>
                  </div>
                  <div><StatusPill status={q.status} /></div>
                  <div className="text-right text-xs text-white/50 flex items-center md:justify-end gap-1">
                    <Calendar className="h-3 w-3" /> {q.created_at?.slice(0, 10)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {openId && <QuoteDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
};

export default AdminFreightQuotes;
